import { FC, useCallback, useLayoutEffect, useRef, useState } from "react";
import FormLayout from "../../../../../../../components/forms/FormLayout";
import { IModule } from "../../../../../../../interfaces/IFurniture";
import { Box, Button, Card, Divider, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "@emotion/styled";
import { v4 as uuidv4 } from 'uuid';
import sofaAnimation from "../../../../../../../assets/animations/sofa.json";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragOverEvent
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    rectSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useToast } from "../../../../../../../hooks/useToast";
import DimensionHelper from "../../../../../../../helpers/dimension.helper";
import Lottie from "lottie-react";
import CustomerForm from "./components/CustomerForm";
import LoadingAnimation from "../../../../../../../components/_common/Loading";
import furnitureController from "../../../../../../../Api/furnitureController";
import { baseURL } from "../../../../../../../config";

const BASE_URL = baseURL;

const ModulesContainer = styled(Box)`
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
`;

const ModuleImage = styled('img')`
    height: 90px;
    max-width: 100%;
    object-fit: contain;
`;

const Module = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 165px;
    margin: 16px;
    text-align: center;
    background: transparent;
    border-radius: 16px;
    box-shadow: 0 2px 8px 0 #ececec;
    padding: 16px 8px 12px 8px;
    min-width: 140px;
    flex: 1 1 140px;
    position: relative;
`;

const ProjectContainer = styled(Box)`
    min-height: 50vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const DimensionContainer = styled(Box)`
    display: flex;
    font-size: small;
`

const AnimationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
 `

const ModulesGroupContainer = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
 `

interface ValuationsDialogFormProps {
    furnitureId: string,
    furnitureImage: string;
    furnitureLink: string;
    furnitureName: string;
    modules?: IModule[] | null;
}

type OrientedModule = IModule & { uuid: string; x: number; y: number; direction: "row" | "column" | "row-reverse" };

function getOrientedModules(modules: (IModule & { uuid: string })[]): OrientedModule[] {
    let x = 0, y = 0;
    let direction: "row" | "column" | "row-reverse" = "row";
    const result: OrientedModule[] = [];

    for (const mod of modules) {
        result.push({ ...mod, x, y, direction });

        if (mod.moduleType === "corner") {
            if (direction === "row") {
                direction = "column";
            } else if (direction === "column") {
                direction = "row-reverse";
            } else if (direction === "row-reverse") {
                direction = "column";
            }
        }

        if (direction === "row") x++;
        else if (direction === "column") y++;
        else if (direction === "row-reverse") x--;
    }

    return result;
}

type ProjectDimensions = {
    topWidth: number;
    sideDepth: number;
    bottomWidth: number;
    side2Depth: number;
};

function calculateProjectDimensions(orientedModules: OrientedModule[]): ProjectDimensions | null {
    if (orientedModules.length === 0) return null;

    let topWidth = 0;
    let sideDepth = 0;
    let bottomWidth = 0;

    let i = 0;
    for (; i < orientedModules.length; i++) {
        topWidth += orientedModules[i].width;
        if ((orientedModules[i].moduleType === 'corner') || i === orientedModules.length - 1) {
            break;
        }
    }

    if (i === orientedModules.length - 1) {
        return { topWidth, sideDepth: 0, bottomWidth: 0, side2Depth: 0 };
    }

    i++; 
    if (i < orientedModules.length) {
        sideDepth += orientedModules[i - 1].depth; 
        for (; i < orientedModules.length; i++) {
            sideDepth += orientedModules[i].width;
            if ((orientedModules[i].moduleType === 'corner') || i === orientedModules.length - 1) {
                break;
            }
        }
    }

    if (i === orientedModules.length - 1) {
        return { topWidth, sideDepth, bottomWidth: 0, side2Depth: 0 };
    }

    i++; 
    if (i < orientedModules.length) {
        bottomWidth += orientedModules[i - 1].depth; 
        for (; i < orientedModules.length; i++) {
            bottomWidth += orientedModules[i].width;
        }
    }

    return { topWidth, sideDepth, bottomWidth, side2Depth: 0 };
}

const DimensionLine = ({
    value,
    length,
    orientation = "horizontal",
    labelRight = false,
}: {
    value: number;
    length: number;
    orientation?: "horizontal" | "vertical";
    labelRight?: boolean;
}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: orientation === "horizontal" ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            width: orientation === "horizontal" ? `${length}px` : "auto",
            height: orientation === "vertical" ? `${length}px` : "auto",
        }}
    >
        {orientation === "vertical" && labelRight ? (
            <>
                <Box
                    sx={{
                        width: 2,
                        height: "100%",
                        background: "#1976d2",
                        borderRadius: 1,
                        margin: "0 auto",
                        zIndex: 1,
                    }}
                />
                <Typography
                    sx={{
                        position: "absolute",
                        left: "calc(100% + 8px)",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#1976d2",
                        background: "#fff",
                        px: 1,
                        zIndex: 2,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    {value} cm
                </Typography>
            </>
        ) : (
            <>
                <Typography
                    sx={{
                        position: "absolute",
                        left: orientation === "horizontal" ? "50%" : "50%",
                        top: orientation === "vertical" ? -18 : "-12px",
                        transform: "translate(-50%, -50%)",
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#1976d2",
                        background: "#fff",
                        px: 1,
                        zIndex: 2,
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {value} cm
                </Typography>
                <Box
                    sx={{
                        width: orientation === "horizontal" ? "100%" : 2,
                        height: orientation === "vertical" ? "100%" : 2,
                        background: "#1976d2",
                        borderRadius: 1,
                        margin: "0 auto",
                        zIndex: 1,
                    }}
                />
            </>
        )}
    </Box>
);

const SortableModule = ({
    module,
    handleRemoveModule,
}: any) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: module.uuid });

    let rotate = 0;
    if (module.direction === "column") rotate = 90;
    if (module.direction === "row-reverse") rotate = 180;

    return (
        <Box
            ref={setNodeRef}
            sx={{
                width: 90,
                height: 90,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                cursor: 'grab',
                background: 'transparent',
                boxShadow: 'none',
                border: 'none',
                p: 0,
                m: 0,
                transform: transform ? CSS.Transform.toString(transform) : undefined,
                transition,
                zIndex: transform ? 1301 : 'auto',
            }}
        >
            <IconButton
                size="small"
                onClick={() => handleRemoveModule(module)}
                sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    background: "#fff",
                    zIndex: 2,
                    boxShadow: 1,
                    "&:hover": { background: "#f5f5f5" }
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
            <img
                src={BASE_URL + module.image}
                alt={module.name}
                {...attributes}
                {...listeners}
                style={{
                    width: "80px",
                    height: "80px",
                    objectFit: "contain",
                    borderRadius: 6,
                    background: "#fff",
                    transform: `rotate(${rotate}deg)`,
                    transition: "transform 0.2s",
                    touchAction: "none"
                }}
            />
        </Box>
    );
};

const ValuationsDialogForm: FC<ValuationsDialogFormProps> = ({
    furnitureId,
    furnitureImage,
    furnitureLink,
    furnitureName,
    modules,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [allModules, setAllModules] = useState<(IModule & { uuid: string })[]>(modules?.map(m => ({ ...m, uuid: uuidv4() })) || []);
    const [projectModules, setProjectModules] = useState<(IModule & { uuid: string })[]>([]);
    const [standAloneModules, setStandaloneModules] = useState<(IModule & { uuid: string })[]>([]);
    const [draggedOrder, setDraggedOrder] = useState<(IModule & { uuid: string })[] | null>(null);
    const [projectDimensions, setProjectDimensions] = useState<ProjectDimensions | null>(null);
    const [showCustomerForm, setShowCustomerForm] = useState<boolean>(false);
    const toast = useRef(useToast()).current;
    const customerFormRef = useRef<HTMLDivElement>(null);

    const handleAddModule = (module: IModule) => {
        const cornersCount = projectModules.filter(m => m.moduleType === 'corner').length;
        if (module.moduleType === 'corner' && cornersCount >= 2) {
            toast.error("Możesz dodać maksymalnie 2 moduły narożne.");
            return;
        }
        if (module.moduleType === 'solo') {
            setStandaloneModules(prev => [...prev, { ...module, uuid: uuidv4() }]);
            return;
        }
        setProjectModules(prev => {
            const newModules = [...prev, { ...module, uuid: uuidv4() }];
            const oriented = getOrientedModules(newModules);
            setProjectDimensions(calculateProjectDimensions(oriented));
            return newModules;
        });
    };

    const handleRemoveModule = (module: IModule & { uuid?: string }) => {
        setProjectModules(prev => {
            const newModules = prev.filter(m => m.uuid !== module.uuid);
            const oriented = getOrientedModules(newModules);
            setProjectDimensions(calculateProjectDimensions(oriented));
            return newModules;
        });
    };

    const handleRemoveStandAlone = (uuid: string) => {
        setStandaloneModules(prev => prev.filter(m => m.uuid !== uuid));
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
    );

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            setDraggedOrder(null);
            return;
        }
        const current = draggedOrder || projectModules;
        const oldIndex = current.findIndex(i => i.uuid === active.id);
        const newIndex = current.findIndex(i => i.uuid === over.id);

        // Blokada: nie pozwalaj przesuwać narożnych na środek
        const isCorner = current[oldIndex]?.moduleType === 'corner';
        if (
            isCorner &&
            newIndex !== 0 &&
            newIndex !== current.length - 1
        ) {
            return;
        }

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            setDraggedOrder(arrayMove(current, oldIndex, newIndex));
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setProjectModules((items) => {
                const oldIndex = items.findIndex(i => i.uuid === active.id);
                const newIndex = items.findIndex(i => i.uuid === over?.id);
                const newOrder = arrayMove(items, oldIndex, newIndex);
                const oriented = getOrientedModules(newOrder);
                setProjectDimensions(calculateProjectDimensions(oriented));
                return newOrder;
            });
        } else {
            const oriented = getOrientedModules(projectModules);
            setProjectDimensions(calculateProjectDimensions(oriented));
        }
        setDraggedOrder(null);
    };

    const modulesToShow = draggedOrder || projectModules;
    const orientedModules = getOrientedModules(modulesToShow);

    const modulesNamesLine = orientedModules.length > 0
        ? orientedModules.map(m => m.name).join('+')
        : '';

    const xs = orientedModules.map(m => m.x);
    const ys = orientedModules.map(m => m.y);
    const minX = Math.min(...xs, 0);
    const maxX = Math.max(...xs, 0);
    const minY = Math.min(...ys, 0);
    const maxY = Math.max(...ys, 0);

    const columns = maxX - minX + 1;
    const rows = maxY - minY + 1;

    // Wyznacz moduły na górze (y == minY) i na dole (y == maxY)
    const topModules = orientedModules.filter(m => m.y === minY);
    const bottomModules = orientedModules.filter(m => m.y === maxY);

    // Długości linii (90px szerokości modułu + 3px gap)
    const getLineLength = (mods: OrientedModule[]) =>
        mods.length > 0 ? mods.length * 90 + (mods.length - 1) * 3 : 0;

    const lineLengthTop = getLineLength(topModules);
    const lineLengthBottom = getLineLength(bottomModules);
    const lineLengthY = rows > 0 ? rows * 90 + (rows - 1) * 3 : 0;

    // Pozycja lewej krawędzi linii względem całego gridu
    const getLeftOffset = (mods: OrientedModule[]) => {
        if (mods.length === 0) return 0;
        const firstCol = Math.min(...mods.map(m => m.x));
        return (firstCol - minX) * (90 + 3);
    };

    // Zwiększ przesunięcie i skróć linie, by nie wystawały po lewej
    const leftOffsetTop = getLeftOffset(topModules) + 20;
    const leftOffsetBottom = getLeftOffset(bottomModules) + 20;


    const handleClickValuation = () => {
        if (projectModules.length === 0 && standAloneModules.length === 0) {
            return toast.error("Dodaj moduły do projektu przed wyceną.")
        };
        setShowCustomerForm(true);
        setTimeout(() => {
            customerFormRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    }


    const getModules = useCallback(async (furnitureId: string) => {
        const res = await furnitureController.getFurnitureModules(furnitureId)
        if (!res.ok || !res.modules) {
            setLoading(false);
            return toast.error("Nie udało się pobrać modułów.");
        }
        const modulesWithUuid = res.modules.map(m => ({
            ...m,
            uuid: uuidv4(),
        }));

        setAllModules(modulesWithUuid);
        setLoading(false)
    }, [toast])


    useLayoutEffect(() => {
        if (!modules || modules.length === 0 || modules === null) {
            setLoading(true)
            getModules(furnitureId)
        }
    }, [modules, furnitureId, getModules])



    if (loading) return <Card><LoadingAnimation loadingText="Wczytywanie modułów" /></Card>

    return (
        <FormLayout
            title={`WYCENA ${furnitureName ?? ''}`}
            disableClose
            maxWidth='xl'
            onClickButton={showCustomerForm ? undefined : () => handleClickValuation()}
            buttonText="Wyceń"
        >
            <Divider />
            {/* Kontener na projekt tylko jeśli są moduły */}
            {projectModules.length > 0 && (
                <ProjectContainer>
                    <Box
                        sx={{
                            m: 2,
                            fontWeight: 600,
                            fontSize: 18,
                            textAlign: 'center',
                            letterSpacing: 1,
                            flexWrap: 'wrap',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            maxWidth: 700,
                            lineHeight: 1.2,
                            mt: 4,
                        }}
                    >
                        {modulesNamesLine.replace(/\+/g, ' + ')}
                    </Box>
                    {/* Linie wymiarów */}
                    <Box
                        sx={{
                            position: "relative",
                            width: columns * 90 + (columns - 1) * 3 + 24 || 1,
                            margin: "0 auto",
                            mt: 4,
                            mb: 4,
                            p: 0,
                            background: "#fff",
                            borderRadius: 2,
                            boxShadow: 'none',
                            minHeight: 120,
                            minWidth: 120,
                            display: "inline-block",
                        }}
                    >
                        {/* Górna linia */}
                        {projectDimensions && projectDimensions.topWidth > 0 && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: leftOffsetTop,
                                    top: -10,
                                    zIndex: 10,
                                    width: lineLengthTop
                                }}
                            >
                                <DimensionLine
                                    value={projectDimensions.topWidth}
                                    length={lineLengthTop}
                                    orientation="horizontal"
                                />
                            </Box>
                        )}
                        {/* Boczna linia po PRAWEJ */}
                        {projectDimensions && projectDimensions.sideDepth > 0 && (
                            <Box sx={{ position: "absolute", right: 0, top: 10, bottom: 10, zIndex: 10 }}>
                                <DimensionLine
                                    value={projectDimensions.sideDepth}
                                    length={lineLengthY - 20}
                                    orientation="vertical"
                                    labelRight
                                />
                            </Box>
                        )}
                        {/* Dolna linia */}
                        {projectDimensions && projectDimensions.bottomWidth > 0 && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: leftOffsetBottom,
                                    bottom: -10,
                                    zIndex: 10,
                                    width: lineLengthBottom
                                }}
                            >
                                <DimensionLine
                                    value={projectDimensions.bottomWidth}
                                    length={lineLengthBottom}
                                    orientation="horizontal"
                                />
                            </Box>
                        )}
                        {/* Grid z modułami */}
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={projectModules.map(m => m.uuid)}
                                strategy={rectSortingStrategy}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: `repeat(${columns}, 1fr)`,
                                        gridAutoRows: 'minmax(90px, auto)',
                                        gap: 0,
                                        justifyItems: 'center',
                                        alignItems: 'center',
                                        minHeight: '100px',
                                        width: 'auto',
                                        margin: '0 auto',
                                        position: "relative",
                                        zIndex: 2,
                                        p: 0
                                    }}
                                >
                                    {orientedModules.length > 0 ? (
                                        orientedModules.map((module) => (
                                            <Box
                                                key={module.uuid}
                                                sx={{
                                                    gridColumn: module.x - minX + 1,
                                                    gridRow: module.y - minY + 1,
                                                    background: "transparent",
                                                    boxShadow: "none",
                                                    border: "none",
                                                    p: 0,
                                                    m: 0,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    width: 90,
                                                    height: 90
                                                }}
                                            >
                                                <SortableModule
                                                    module={module}
                                                    handleRemoveModule={handleRemoveModule}
                                                />
                                            </Box>
                                        ))
                                    ) : (
                                        <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>
                                            <AnimationContainer>
                                                <div>
                                                    <Lottie
                                                        style={{
                                                            maxWidth: '250px',
                                                            maxHeight: '250px',
                                                            padding: '50px',
                                                            paddingBottom: '50px',
                                                        }}
                                                        animationData={sofaAnimation}
                                                        loop={true}
                                                    />
                                                    <Typography sx={{ paddingBottom: '50px' }}>
                                                        Dodaj moduły, układaj od lewej strony
                                                    </Typography>
                                                </div>
                                            </AnimationContainer>
                                        </Box>
                                    )}
                                </Box>
                            </SortableContext>
                        </DndContext>
                    </Box>
                </ProjectContainer>
            )}

            {/* Kontener na wolnostojące moduły */}
            {standAloneModules.length > 0 && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 900,
                            mx: "auto",
                            mb: 4,
                            p: { xs: 1, md: 2 },
                            bgcolor: "#fff",
                            borderRadius: 3,
                            boxShadow: "none",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                textAlign: "center",
                                letterSpacing: 1,
                            }}
                        >
                            Twoje moduły wolnostojące
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: { xs: 2, md: 3 },
                                justifyContent: "center",
                                width: "100%",
                            }}
                        >
                            {standAloneModules.map(module => (
                                <Module
                                    key={module.uuid}
                                    sx={{
                                        background: "transparent",
                                        boxShadow: "none",
                                        position: "relative",
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveStandAlone(module.uuid)}
                                        sx={{
                                            position: "absolute",
                                            top: 4,
                                            right: 4,
                                            background: "#fff",
                                            zIndex: 2,
                                            boxShadow: 1,
                                            "&:hover": { background: "#f5f5f5" }
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                    <ModuleImage src={BASE_URL + module.image} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
                                        {module.name}
                                    </Typography>
                                    <DimensionContainer sx={{ mt: 0.5, color: "#1976d2" }}>
                                        {DimensionHelper.formatDimensions(module.width, module.depth, module.height)}
                                    </DimensionContainer>
                                    <DimensionContainer sx={{ mt: 0.5, color: "#555" }}>
                                        {module.description}
                                    </DimensionContainer>
                                </Module>
                            ))}
                        </Box>
                    </Box>
                </>
            )}

            {showCustomerForm ? (
                <div ref={customerFormRef}>
                    <CustomerForm
                        onBackToProject={() => setShowCustomerForm(false)}
                        modules={projectModules}
                        standAloneModules={standAloneModules}
                        furnitureId={furnitureId}
                        furnitureImage={furnitureImage}
                        furnitureLink={furnitureLink}
                        furnitureName={furnitureName}
                    />
                </div>
            ) : (
                <>
                    <Divider
                        sx={{
                            marginTop: projectModules.length === 0 && standAloneModules.length === 0 ? '50vh' : 0,
                        }}
                    />
                    <Divider />
                    <Divider />
                    <Divider />
                    <Divider />
                    <Divider />
                    <Divider />
                    <Typography sx={{ textAlign: 'center', mt: 2 }} variant='h4'>Dostępne moduły:</Typography>
                    <ModulesContainer>
                        {([
                            { type: "left", label: "Moduły lewe", color: undefined },
                            { type: "middle", label: "Moduły wewnętrzne", color: undefined },
                            { type: "right", label: "Moduły prawe", color: undefined },
                            { type: "corner", label: "Moduły narożne", color: undefined },
                            { type: "solo", label: "Moduły wolnostojące", color: undefined },
                            { type: undefined, label: "Moduły bez typu", color: "#b71c1c" }
                        ] as const).map(group => {
                            const groupModules = Array.isArray(allModules)
                                ? allModules.filter(m => group.type ? m.moduleType === group.type : !m.moduleType)
                                : [];
                            if (groupModules.length === 0) return null;
                            return (
                                <div key={group.label}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            mt: 2,
                                            mb: 1,
                                            fontWeight: 700,
                                            color: group.color
                                        }}
                                    >
                                        {group.label}
                                    </Typography>
                                    <ModulesGroupContainer>
                                        {groupModules.map(module => (
                                            <Module key={module._id}>
                                                <ModuleImage src={BASE_URL + module.image} />
                                                <div>{module.name}</div>
                                                <DimensionContainer>
                                                    {DimensionHelper.formatDimensions(module.width, module.depth, module.height)}
                                                </DimensionContainer>
                                                <DimensionContainer>
                                                    {module.description}
                                                </DimensionContainer>
                                                <Button
                                                    onClick={() => handleAddModule(module)}
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                >
                                                    Dodaj moduł{group.type === "corner" ? " (narożnik)" : ""}
                                                </Button>
                                            </Module>
                                        ))}
                                    </ModulesGroupContainer>
                                    {group.type !== undefined && <Divider sx={{ width: "100%", my: 2 }} />}
                                </div>
                            );
                        })}
                    </ModulesContainer>
                </>
            )}
        </FormLayout>
    );
};

export default ValuationsDialogForm;